// drawPreviewStyles.js — Shared OL Style constants for bespoke draw-interaction sketch
// previews (the little blue dots/lines and amber dashed "width indicator" strokes shown
// while a user is mid-placement of a multi-click tactical graphic).
//
// These were previously redefined near-identically in ~7 customInteractions/*.js files
// (miscBespokeTools, lineSpineWidthTools, lineArcSideTools, areaGeometricTools,
// areaPolygonTools, areaRadialTools, areaSpecialTools). Consolidated here as the single
// source of truth; minor param differences between the original per-file copies (radius 4
// vs 5, width 1.5 vs 2) were unified onto the values used by the majority of call sites —
// the visual difference is negligible and not worth keeping as separate exports.

import { Style, Stroke, Fill, Circle as CircleStyle } from 'ol/style';

// Solid blue vertex dot — the standard "a point has been placed here" marker.
export const PREVIEW_DOT_BLUE = new Style({
    image: new CircleStyle({ radius: 5, fill: new Fill({ color: '#3b82f6' }), stroke: new Stroke({ color: '#fff', width: 1.5 }) }),
});

// Solid amber vertex dot — used for a point that has a different semantic role than the
// main spine/vertex points (e.g. AXIS's width point, AREA2's LAA anchor).
export const PREVIEW_DOT_AMBER = new Style({
    image: new CircleStyle({ radius: 4, fill: new Fill({ color: '#f59e0b' }), stroke: new Stroke({ color: '#fff', width: 1.5 }) }),
});

// Solid green vertex dot — the "direction/target" role (e.g. a directional arrow's tip/target
// click, as distinct from its anchor/rear click) — see ROLE_DOT_STYLES / getRoleDotStyle below.
export const PREVIEW_DOT_GREEN = new Style({
    image: new CircleStyle({ radius: 4, fill: new Fill({ color: '#10b981' }), stroke: new Stroke({ color: '#fff', width: 1.5 }) }),
});

// Faint semi-transparent blue dot — for the live cursor position (not yet a placed point).
export const PREVIEW_DOT_CURSOR = new Style({
    image: new CircleStyle({ radius: 4, fill: new Fill({ color: 'rgba(59,130,246,0.4)' }), stroke: new Stroke({ color: '#3b82f6', width: 1 }) }),
});

// Solid blue line — the main spine/segment/polygon-outline sketch stroke.
export const PREVIEW_LINE_BLUE = new Style({ stroke: new Stroke({ color: '#3b82f6', width: 2 }) });

// Dashed blue line — used where the blue line needs to read as "provisional/outline" rather
// than a committed spine (e.g. capped-polygon sketch outlines).
export const PREVIEW_LINE_BLUE_DASHED = new Style({ stroke: new Stroke({ color: '#3b82f6', width: 2, lineDash: [5, 3] }) });

// Dashed green line — second-zone variant (AREA26's paired-polygon zone 2).
export const PREVIEW_LINE_GREEN_DASHED = new Style({ stroke: new Stroke({ color: '#10b981', width: 2, lineDash: [5, 3] }) });

// Dashed amber line — the standard "width/arc/side indicator" bracket shown once a spine or
// baseline has been placed and the user is now picking a perpendicular offset.
export const PREVIEW_LINE_AMBER_DASHED = new Style({ stroke: new Stroke({ color: '#f59e0b', width: 2, lineDash: [5, 3] }) });

// Faint dashed blue line — a subtle helper line (e.g. perpendicular guide from spine
// midpoint to cursor) that shouldn't visually compete with the main amber width indicator.
export const PREVIEW_LINE_FAINT_BLUE_DASHED = new Style({ stroke: new Stroke({ color: 'rgba(59,130,246,0.35)', width: 1, lineDash: [3, 4] }) });

// ── Role-aware handle styling (ticket 04) ──────────────────────────────────────────────────
// Every bespoke multi-point collector (see multiPointCollector.js) tags each placed-point
// marker with the semantic role it plays in that DrawRule's point order — 'anchor' (a base,
// centerline, or corner point with no directional meaning of its own), 'direction' (a tip,
// target, or bearing point the user is aiming at), or 'width' (a side/offset point that
// controls a perpendicular distance rather than the shape's outline). Centralizing the
// role -> marker mapping here means every migrated tool's handles are visually
// distinguishable by role in the same way, replacing the ad hoc "just make every dot the same
// blue" default most per-file Style literals used before this ticket.
export const ROLE_DOT_STYLES = {
    anchor: PREVIEW_DOT_BLUE,
    direction: PREVIEW_DOT_GREEN,
    width: PREVIEW_DOT_AMBER,
};

/**
 * @param {'anchor'|'direction'|'width'} role
 * @returns {import('ol/style').Style} — falls back to the anchor style for an unknown role
 *   rather than throwing, so a typo'd role degrades to "looks like every other point" instead
 *   of crashing the whole draw interaction.
 */
export function getRoleDotStyle(role) {
    return ROLE_DOT_STYLES[role] ?? PREVIEW_DOT_BLUE;
}
